/**
 * File validation utilities for upload functionality.
 */

import {
  DEFAULT_UPLOAD_CONFIG,
  UploadConfig,
  getAllowedMimeTypes,
  getFileCategory,
  getEffectiveMimeType,
} from '../types/upload';

/**
 * Result of file validation.
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a file against upload constraints.
 */
export function validateFile(
  file: File,
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): ValidationResult {
  // Check file size
  if (file.size > config.maxFileSize) {
    const maxMB = config.maxFileSize / (1024 * 1024);
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File "${file.name}" is too large (${fileMB} MB). Maximum size is ${maxMB} MB.`,
    };
  }

  // Check file type - use effective MIME type (with extension-based fallback)
  // This handles cases where browsers send application/octet-stream for known types like .md
  const effectiveMimeType = getEffectiveMimeType(file);
  const allowedTypes = getAllowedMimeTypes(config);
  if (!allowedTypes.includes(effectiveMimeType)) {
    const category = getFileCategory(effectiveMimeType, config);
    if (category === 'unknown') {
      return {
        valid: false,
        error: `File type "${effectiveMimeType || 'unknown'}" is not supported. Please upload images (PNG, JPEG, GIF, WebP) or documents (PDF, TXT, MD, DOCX).`,
      };
    }
  }

  // Check for empty files
  if (file.size === 0) {
    return {
      valid: false,
      error: `File "${file.name}" is empty.`,
    };
  }

  return { valid: true };
}

/**
 * Validate multiple files at once.
 * Returns errors for any invalid files.
 */
export function validateFiles(
  files: File[],
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): { validFiles: File[]; errors: string[] } {
  const validFiles: File[] = [];
  const errors: string[] = [];

  // Check total count
  if (files.length > config.maxFilesPerMessage) {
    errors.push(
      `Too many files selected (${files.length}). Maximum is ${config.maxFilesPerMessage} files per message.`
    );
    // Still validate individual files up to the limit
    files = files.slice(0, config.maxFilesPerMessage);
  }

  for (const file of files) {
    const result = validateFile(file, config);
    if (result.valid) {
      validFiles.push(file);
    } else if (result.error) {
      errors.push(result.error);
    }
  }

  return { validFiles, errors };
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  // Show more precision for smaller sizes
  if (i === 0) return `${bytes} B`;
  if (i === 1) return `${size.toFixed(1)} KB`;
  if (i === 2) return `${size.toFixed(2)} MB`;
  return `${size.toFixed(2)} GB`;
}

/**
 * Generate a unique file ID for client-side tracking.
 */
export function generateFileId(): string {
  return `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get file extension from filename.
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot + 1).toLowerCase();
}

/**
 * Truncate filename for display, preserving extension.
 */
export function truncateFilename(
  filename: string,
  maxLength: number = 25
): string {
  if (filename.length <= maxLength) return filename;

  const ext = getFileExtension(filename);
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));

  // Reserve space for extension + ellipsis
  const availableLength = maxLength - ext.length - 4; // "..." + "."
  if (availableLength <= 3) {
    // Very short max length, just truncate
    return filename.substring(0, maxLength - 3) + '...';
  }

  return `${nameWithoutExt.substring(0, availableLength)}...${ext ? '.' + ext : ''}`;
}

/**
 * Get accept attribute value for file input.
 */
export function getAcceptAttribute(
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): string {
  return getAllowedMimeTypes(config).join(',');
}

/**
 * Check if browser supports File API.
 */
export function isFileApiSupported(): boolean {
  return !!(window.File && window.FileReader && window.FileList && window.Blob);
}

/**
 * Create a preview URL for an image file.
 * Returns a Promise that resolves to a data URL.
 */
export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Get icon name for a file type.
 */
export function getFileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'picture_as_pdf';
  if (mimeType === 'text/plain') return 'description';
  if (mimeType === 'text/markdown') return 'article';
  if (mimeType.includes('wordprocessingml')) return 'description';
  return 'insert_drive_file';
}
